import React, { useEffect, useMemo, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import {
  Loader2, Plus, Save, Trash2, X, LogOut, Search,
  Users, Sparkles, Star, UserCheck, UserX, Newspaper, History, UserCog, Award, ShieldCheck, Settings,
  MessageSquare, BotMessageSquare, Image as ImageIcon, FileText, Twitter, Briefcase, LifeBuoy
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { adminApi, api as publicApi, API, axios } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { useToast } from "../hooks/use-toast";
import NewsPanel from "../components/admin/NewsPanel";
import TeamPanel from "../components/admin/TeamPanel";
import TimelinePanel from "../components/admin/TimelinePanel";
import UsersPanel from "../components/admin/UsersPanel";
import ShopPanel from "../components/admin/ShopPanel";
import BadgesPanel from "../components/admin/BadgesPanel";
import SettingsPanel from "../components/admin/SettingsPanel";
import EventsPanel from "../components/admin/EventsPanel";
import AuditLogPanel from "../components/admin/AuditLogPanel";
import AiNewsPanel from "../components/admin/AiNewsPanel";
import RaresPanel from "../components/admin/RaresPanel";
import FurnituresPanel from "../components/admin/FurnituresPanel";
import ReviewerPanel from "../components/admin/ReviewerPanel";
import BackgroundsPanel from "../components/admin/BackgroundsPanel";
import TeamChatPanel from "../components/admin/TeamChatPanel";
import MediaPanel from "../components/admin/MediaPanel";
import PromoDesignerPanel from "../components/admin/PromoDesignerPanel";
import FurniLatestPanel from "../components/admin/FurniLatestPanel";
import HabboImagesPanel from "../components/admin/HabboImagesPanel";
import HeroPanel from "../components/admin/HeroPanel";
import TwitterPanel from "../components/admin/TwitterPanel";
import JobsPanel from "../components/admin/JobsPanel";
import ApplicationsPanel from "../components/admin/ApplicationsPanel";
import SupportPanel from "../components/admin/SupportPanel";

// Tab-Gruppen: gibt eine bessere Uebersicht im Admin-Dashboard.
// Jeder Tab definiert minimale Permission ('admin' = nur Vollzugang).
const TAB_GROUPS = [
  {
    label: "Team",
    items: [
      { id: "chat", label: "Team-Chat", icon: MessageSquare, perm: "any-write" },
      { id: "team", label: "Team-Mitglieder", icon: UserCog, perm: "admin" },
      { id: "users", label: "User & Rechte", icon: ShieldCheck, perm: "admin" },
    ],
  },
  {
    label: "News",
    items: [
      { id: "news", label: "Artikel", icon: Newspaper, perm: "news_write" },
      { id: "ai-news", label: "KI-Import", icon: BotMessageSquare, perm: "news_write" },
      { id: "reviewer", label: "Review", icon: FileText, perm: "news_review" },
      { id: "timeline", label: "Historie", icon: History, perm: "admin" },
      { id: "media", label: "Medien", icon: ImageIcon, perm: "media_access" },
      { id: "habbo-images", label: "Habbo-Bilder", icon: ImageIcon, perm: "media_access" },
      { id: "promo-designer", label: "Promo-Designer", icon: Sparkles, perm: "media_access" },
      { id: "furni-latest", label: "Neue Moebel", icon: Sparkles, perm: "media_access" },
    ],
  },
  {
    label: "Inhalte",
    items: [
      { id: "events", label: "Events", icon: Sparkles, perm: "events_edit" },
      { id: "projects", label: "Projekte", icon: Sparkles, perm: "projects_edit" },
      { id: "badges", label: "Badges", icon: Award, perm: "badges_edit" },
      { id: "rares", label: "Rares", icon: Star, perm: "rares_edit" },
      { id: "furniture", label: "Furniture", icon: Star, perm: "furniture_edit" },
      { id: "habbos", label: "Habbos", icon: Users, perm: "admin" },
    ],
  },
  {
    label: "Community",
    items: [
      { id: "jobs", label: "Jobs", icon: Briefcase, perm: "jobs_manage" },
      { id: "applications", label: "Bewerbungen", icon: FileText, perm: "jobs_manage" },
      { id: "support", label: "Support", icon: LifeBuoy, perm: "support_manage" },
    ],
  },
  {
    label: "Shop",
    items: [
      { id: "shop", label: "Shop-Items", icon: ShieldCheck, perm: "admin" },
      { id: "backgrounds", label: "Hintergründe", icon: ImageIcon, perm: "admin" },
    ],
  },
  {
    label: "System",
    items: [
      { id: "hero", label: "Startseite-Habbos", icon: Sparkles, perm: "admin" },
      { id: "twitter", label: "Twitter / X", icon: Twitter, perm: "admin" },
      { id: "audit", label: "Audit-Log", icon: ShieldCheck, perm: "admin" },
      { id: "settings", label: "Einstellungen", icon: Settings, perm: "admin" },
    ],
  },
];

// Flache Tab-Liste fuer ID-Lookup
const ALL_TABS = TAB_GROUPS.flatMap((g) => g.items);

const AdminDashboard = () => {
  const { adminToken, adminName, adminLogout, userToken, user, ready } = useAuth();

  // Token waehlen: bevorzugt Admin, sonst User (mit admin_access)
  const userHasAdminAccess = !!(user && user.permissions && user.permissions.admin_access);
  const token = adminToken || (userHasAdminAccess ? userToken : null);
  const isAdmin = !!adminToken;
  const username = adminName || user?.habbo_name;

  const perms = useMemo(() => {
    if (isAdmin) return { admin: true, news_write: true, news_review: true, news_sticky: true, events_edit: true, projects_edit: true, badges_edit: true, rares_edit: true, furniture_edit: true, ai_import: true, media_access: true, jobs_manage: true, support_manage: true, "any-write": true };
    const p = (user && user.permissions) || {};
    const anyWrite = !!(p.news_write || p.news_review || p.events_edit || p.projects_edit || p.badges_edit || p.rares_edit || p.furniture_edit);
    return {
      admin: false,
      news_write: !!p.news_write,
      news_review: !!p.news_review,
      news_sticky: !!p.news_sticky,
      events_edit: !!p.events_edit,
      projects_edit: !!p.projects_edit,
      badges_edit: !!p.badges_edit,
      rares_edit: !!p.rares_edit,
      furniture_edit: !!p.furniture_edit,
      ai_import: !!p.ai_import,
      media_access: !!p.media_access,
      jobs_manage: !!p.jobs_manage,
      support_manage: !!p.support_manage,
      "any-write": anyWrite,
    };
  }, [isAdmin, user]);

  const isTabVisible = (t) => (t.perm === "admin" ? isAdmin : (isAdmin || perms[t.perm]));

  const visibleGroups = useMemo(() =>
    TAB_GROUPS
      .map((g) => ({ ...g, items: g.items.filter(isTabVisible) }))
      .filter((g) => g.items.length > 0),
    [isAdmin, perms]
  );

  const visibleTabs = useMemo(
    () => visibleGroups.flatMap((g) => g.items),
    [visibleGroups]
  );

  const [tab, setTab] = useState(null);
  const [communityCounts, setCommunityCounts] = useState({});

  useEffect(() => {
    if (!token) return undefined;
    const load = () =>
      axios.get(`${API}/cms/community-counts`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setCommunityCounts(r.data || {}))
        .catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [token, tab]);

  useEffect(() => {
    if (visibleTabs.length > 0 && !tab) {
      // Bevorzugt 'chat' als Start-Tab; sonst erstes verfuegbares
      const chatTab = visibleTabs.find((t) => t.id === "chat");
      setTab(chatTab ? "chat" : visibleTabs[0].id);
    }
  }, [visibleTabs, tab]);

  if (ready && !token) return <Navigate to="/admin/login" replace />;
  if (!ready) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-20 text-center text-zinc-400">
        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-8" data-testid="admin-dashboard">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">Backend</div>
          <h1 className="mt-1 text-3xl font-bold text-white">Admin-Dashboard</h1>
          <p className="mt-1 text-sm text-zinc-400">
            Eingeloggt als <span className="text-white">{username}</span>
            {!isAdmin && <span className="ml-2 rounded bg-[#7c4dff]/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#b39bff]">Power-User</span>}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={adminLogout} data-testid="admin-logout-btn" className="btn-ghost rounded-lg px-3 py-2 text-sm">
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">        {/* Sidebar mit Tab-Gruppen */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
          {visibleGroups.map((group) => (
            <div key={group.label} className="rounded-xl border border-white/10 bg-[#0f141b] p-2">
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map((t) => {
                  const Icon = t.icon;
                  const active = tab === t.id;
                  const badgeCount = t.id === "applications"
                    ? (communityCounts.new_applications || 0)
                    : t.id === "support"
                      ? (communityCounts.open_support || 0)
                      : 0;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      data-testid={`admin-tab-${t.id}`}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        active
                          ? "bg-[#7c4dff]/15 text-white shadow-[inset_0_0_0_1px_rgba(124,77,255,0.35)]"
                          : "text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${active ? "text-[#b39bff]" : ""}`} />
                      <span className="flex-1 text-left">{t.label}</span>
                      {badgeCount > 0 && (
                        <span data-testid={`admin-tab-badge-${t.id}`} className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                          {badgeCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        {/* Content */}
        <div className="min-w-0">
          {tab === "chat" && <TeamChatPanel token={token} />}
          {tab === "news" && <NewsPanel token={token} perms={perms} />}
          {tab === "ai-news" && <AiNewsPanel token={token} />}
          {tab === "events" && <EventsPanel token={token} />}
          {tab === "timeline" && <TimelinePanel token={token} />}
          {tab === "team" && <TeamPanel token={token} />}
          {tab === "habbos" && <HabbosPanel token={token} />}
          {tab === "projects" && <ProjectsPanel token={token} />}
          {tab === "badges" && <BadgesPanel token={token} />}
          {tab === "rares" && <RaresPanel token={token} />}
          {tab === "furniture" && <FurnituresPanel token={token} />}
          {tab === "reviewer" && <ReviewerPanel token={token} />}
          {tab === "backgrounds" && <BackgroundsPanel token={token} />}
          {tab === "media" && <MediaPanel token={token} />}
          {tab === "promo-designer" && <PromoDesignerPanel token={token} />}
          {tab === "habbo-images" && <HabboImagesPanel token={token} />}
          {tab === "furni-latest" && <FurniLatestPanel token={token} onOpenInPromo={() => setTab("promo-designer")} />}
          {tab === "users" && <UsersPanel token={token} />}
          {tab === "shop" && <ShopPanel token={token} />}
          {tab === "hero" && <HeroPanel token={token} />}
          {tab === "twitter" && <TwitterPanel token={token} />}
          {tab === "audit" && <AuditLogPanel token={token} />}
          {tab === "settings" && <SettingsPanel token={token} />}
          {tab === "jobs" && <JobsPanel token={token} />}
          {tab === "applications" && <ApplicationsPanel token={token} />}
          {tab === "support" && <SupportPanel token={token} />}
        </div>
      </div>
    </section>
  );
};


/* ------------------- HABBOS PANEL ------------------- */

const emptyHabbo = { name: "", motto: "", featured: true, hotel: "de" };

const HabbosPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [habbos, setHabbos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyHabbo);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [hotels, setHotels] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [data, hs] = await Promise.all([a.listHabbos(), publicApi.listHotels()]);
      setHabbos(data);
      setHotels(hs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();  }, []);

  const filtered = habbos.filter((h) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return h.name.toLowerCase().includes(s) || (h.motto || "").toLowerCase().includes(s);
  });

  const onCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await a.createHabbo(form);
      setForm(emptyHabbo);
      toast({ title: "Habbo angelegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Anlegen fehlgeschlagen", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const onSave = async (h) => {
    setSavingId(h.id);
    try {
      await a.updateHabbo(h.id, { name: h.name, motto: h.motto, featured: h.featured, hotel: h.hotel || "de" });
      toast({ title: "Gespeichert" });
      setEditingId(null);
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Speichern fehlgeschlagen", variant: "destructive" });
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (h) => {
    if (!window.confirm(`Habbo "${h.name}" wirklich löschen?`)) return;
    try {
      await a.deleteHabbo(h.id);
      toast({ title: "Gelöscht" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const update = (id, field, val) =>
    setHabbos((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  return (
    <div>
      {/* Create-Form */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neuen Habbo anlegen</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1.5fr_2fr_auto_auto_auto]">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Habbo-Name (exakt)"
            data-testid="habbo-create-name"
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <Input
            value={form.motto}
            onChange={(e) => setForm({ ...form, motto: e.target.value })}
            placeholder="Rolle / Beschreibung"
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <select
            value={form.hotel}
            onChange={(e) => setForm({ ...form, hotel: e.target.value })}
            data-testid="habbo-create-hotel"
            className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200 focus:border-[#7c4dff]/50 focus:outline-none"
          >
            {hotels.map((h) => (
              <option key={h.id} value={h.id}>{h.flag} {h.code} – {h.label}</option>
            ))}
          </select>
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <Switch
              checked={form.featured}
              onCheckedChange={(v) => setForm({ ...form, featured: v })}
            />
            <span>Hero</span>
          </div>
          <Button onClick={onCreate} disabled={creating || !form.name.trim()} data-testid="habbo-create-btn" className="btn-primary rounded-lg px-4 text-sm">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Anlegen
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Habbos durchsuchen ..."
            className="pl-9 bg-[#0f141b] border-white/10 text-zinc-200"
          />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} / {habbos.length}</div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]">
          <div className="grid grid-cols-[80px_1.5fr_2fr_auto_auto_auto] gap-3 border-b border-white/10 bg-[#0a0d12] px-4 py-2 text-[11px] uppercase tracking-wider text-zinc-500">
            <div>Avatar</div>
            <div>Name</div>
            <div>Motto / Rolle</div>
            <div className="text-center">Hotel</div>
            <div className="text-center">Hero</div>
            <div className="text-right">Aktionen</div>
          </div>
          {filtered.map((h) => {
            const editing = editingId === h.id;
            const hotelKey = h.hotel || "de";
            const hotelMeta = hotels.find((x) => x.id === hotelKey);
            const hotelDomain = (hotelMeta && hotelMeta.domain) ? hotelMeta.domain : "habbo.de";
            const avatar = `https://www.${hotelDomain}/habbo-imaging/avatarimage?user=${encodeURIComponent(h.name)}&direction=2&head_direction=3&size=m&action=std&gesture=sml`;
            return (
              <div key={h.id} className="grid grid-cols-[80px_1.5fr_2fr_auto_auto_auto] items-center gap-3 border-b border-white/5 px-4 py-2 last:border-b-0">
                <div className="flex justify-center">
                  <img src={avatar} alt={h.name} className="hb-pixel-img h-14" loading="lazy" />
                </div>
                <div>
                  {editing ? (
                    <Input
                      value={h.name}
                      onChange={(e) => update(h.id, "name", e.target.value)}
                      className="bg-[#10141b] border-white/10 text-zinc-200"
                    />
                  ) : (
                    <div className="font-semibold text-white">{h.name}</div>
                  )}
                </div>
                <div>
                  {editing ? (
                    <Input
                      value={h.motto}
                      onChange={(e) => update(h.id, "motto", e.target.value)}
                      className="bg-[#10141b] border-white/10 text-zinc-200"
                    />
                  ) : (
                    <div className="text-sm text-zinc-300">{h.motto || <span className="text-zinc-500">–</span>}</div>
                  )}
                </div>
                <div className="flex justify-center">
                  {editing ? (
                    <select
                      value={hotelKey}
                      onChange={(e) => update(h.id, "hotel", e.target.value)}
                      data-testid={`habbo-edit-hotel-${h.id}`}
                      className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1 text-xs text-zinc-200"
                    >
                      {hotels.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.flag} {opt.code}</option>
                      ))}
                    </select>
                  ) : (
                    <span title={hotelMeta?.label || hotelKey} className="text-base">{hotelMeta?.flag || "🌐"}</span>
                  )}
                </div>
                <div className="flex justify-center">
                  {editing ? (
                    <Switch checked={h.featured} onCheckedChange={(v) => update(h.id, "featured", v)} />
                  ) : h.featured ? (
                    <span title="Im Hero sichtbar"><Star className="h-4 w-4 text-[#ffb84d]" /></span>
                  ) : (
                    <span title="Nicht im Hero" className="text-zinc-600">–</span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-1">
                  {editing ? (
                    <>
                      <Button onClick={() => onSave(h)} disabled={savingId === h.id} className="btn-primary rounded-lg px-2 py-1 text-xs">
                        {savingId === h.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      </Button>
                      <Button onClick={() => { setEditingId(null); load(); }} className="btn-ghost rounded-lg px-2 py-1 text-xs">
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => setEditingId(h.id)} className="btn-ghost rounded-lg px-2 py-1 text-xs">
                        Bearbeiten
                      </Button>
                      <Button onClick={() => onDelete(h)} className="btn-ghost rounded-lg px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-10 text-center text-sm text-zinc-500">Keine Habbos gefunden.</div>
          )}
        </div>
      )}
    </div>
  );
};


/* ------------------- PROJECTS PANEL ------------------- */

const emptyProject = { slug: "", title: "", description: "", icon: "users", order: 0, badge_code: "", badge_color: "#7c4dff" };

const ProjectsPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [creating, setCreating] = useState(false);
  const [newMember, setNewMember] = useState({});
  const badgeLookupTimers = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const [data, hs, allShop] = await Promise.all([
        a.listProjects(),
        publicApi.listHotels(),
        publicApi.listShop(),
      ]);
      setProjects(data);
      setHotels(hs);
      setBackgrounds((allShop || []).filter((i) => i.type === "background"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();  }, []);

  const onUpdateMember = async (p, m, patch) => {
    try {
      await a.updateMember(p.id, m.id, {
        habbo_name: m.habbo_name,
        role: m.role,
        hotel: m.hotel || "de",
        background_image_url: m.background_image_url || null,
        ...patch,
      });
      toast({ title: "Hintergrund gespeichert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const onCreate = async () => {
    if (!form.slug.trim() || !form.title.trim()) return;
    setCreating(true);
    try {
      await a.createProject({ ...form, members: [] });
      setForm(emptyProject);
      toast({ title: "Projekt angelegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Anlegen fehlgeschlagen", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const onUpdateProject = async (p) => {
    try {
      await a.updateProject(p.id, {
        slug: p.slug, title: p.title, description: p.description,
        icon: p.icon, order: p.order,
        badge_code: (p.badge_code || "").trim().toUpperCase(),
        badge_color: p.badge_color || "#7c4dff",
        members: p.members || [],
      });
      toast({ title: "Projekt gespeichert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const onDeleteProject = async (p) => {
    if (!window.confirm(`Projekt "${p.title}" wirklich löschen?`)) return;
    try {
      await a.deleteProject(p.id);
      toast({ title: "Gelöscht" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const onAddMember = async (p) => {
    const draft = newMember[p.id] || {};
    if (!draft.habbo_name?.trim() || !draft.role?.trim()) return;
    try {
      await a.addMember(p.id, {
        habbo_name: draft.habbo_name.trim(),
        role: draft.role.trim(),
        hotel: draft.hotel || "de",
      });
      setNewMember((prev) => ({ ...prev, [p.id]: { habbo_name: "", role: "", hotel: "de" } }));
      toast({ title: "Mitglied hinzugefügt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const onRemoveMember = async (p, m) => {
    if (!window.confirm(`"${m.habbo_name}" aus ${p.title} entfernen?`)) return;
    try {
      await a.removeMember(p.id, m.id);
      toast({ title: "Mitglied entfernt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const updateProj = (id, field, val) =>
    setProjects((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  return (
    <div>
      {/* Create-Form */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neues Projekt anlegen</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="Slug (z.B. community-games)" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Anzeigename" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Icon (users, star, gamepad-2, swords ...)" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} placeholder="Sortier-Reihenfolge" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Kurzbeschreibung" rows={2} className="md:col-span-2 bg-[#10141b] border-white/10 text-zinc-200" />
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={onCreate} disabled={creating || !form.slug.trim() || !form.title.trim()} className="btn-primary rounded-lg px-4 text-sm">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Projekt anlegen
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {projects.map((p) => {
            const open = expanded === p.id;
            return (
              <div key={p.id} className="rounded-xl border border-white/10 bg-[#0f141b]">
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <button onClick={() => setExpanded(open ? null : p.id)} className="flex-1 text-left">
                    <div className="text-sm font-semibold text-white">{p.title}</div>
                    <div className="text-xs text-zinc-400">/{p.slug} · {(p.members || []).length} Mitglieder · #{p.order}</div>
                  </button>
                  <Button onClick={() => onDeleteProject(p)} className="btn-ghost rounded-lg px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {open && (
                  <div className="border-t border-white/10 p-4">
                    {/* Projekt-Felder */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Input value={p.slug} onChange={(e) => updateProj(p.id, "slug", e.target.value)} placeholder="Slug" className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Input value={p.title} onChange={(e) => updateProj(p.id, "title", e.target.value)} placeholder="Titel" className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Input value={p.icon} onChange={(e) => updateProj(p.id, "icon", e.target.value)} placeholder="Icon" className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Input type="number" value={p.order} onChange={(e) => updateProj(p.id, "order", parseInt(e.target.value || "0", 10))} placeholder="Order" className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Textarea value={p.description || ""} onChange={(e) => updateProj(p.id, "description", e.target.value)} placeholder="Beschreibung" rows={2} className="md:col-span-2 bg-[#10141b] border-white/10 text-zinc-200" />
                    </div>

                    {/* Mitglieder-Badge fuer dieses Projekt */}
                    <div className="mt-3 rounded-lg border border-[#7c4dff]/30 bg-[#7c4dff]/5 p-3">
                      <div className="text-xs font-bold uppercase tracking-wider text-[#b39bff]">Projekt-Badge fuer Mitglieder</div>
                      <div className="mt-1 text-[11px] text-zinc-400">
                        Wird ueber dem Avatar im Kommentarbereich angezeigt, mit farbigem Nebel dahinter. Beispiel: AMB fuer Botschafter.
                      </div>
                      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_auto]">
                        <div>
                          <Input
                            value={p.badge_code || ""}
                            onChange={(e) => updateProj(p.id, "badge_code", e.target.value.trim().toUpperCase())}
                            onBlur={(e) => {
                              const code = e.target.value.trim().toUpperCase();
                              if (!code || code === p._lastLookup) return;
                              // Debounce via Timer (max. 1 Lookup pro 350ms pro Projekt)
                              if (badgeLookupTimers.current[p.id]) clearTimeout(badgeLookupTimers.current[p.id]);
                              badgeLookupTimers.current[p.id] = setTimeout(async () => {
                                try {
                                  const data = await publicApi.badgeGamedata(code);
                                  if (data?.name) {
                                    updateProj(p.id, "_lastLookup", code);
                                    updateProj(p.id, "badge_name_de", data.name);
                                    toast({ title: "Badge gefunden", description: data.name });
                                  } else {
                                    toast({ title: "Kein deutscher Name", description: "Code akzeptiert, aber nicht in Gamedata.", variant: "destructive" });
                                  }
                                } catch { /* ignore */ }
                              }, 350);
                            }}
                            placeholder="Badge-Code (z.B. AMB) – Enter fuer Gamedata-Lookup"
                            data-testid={`project-${p.slug}-badge-code`}
                            className="bg-[#10141b] border-white/10 text-zinc-200"
                            maxLength={20}
                          />
                          {p.badge_name_de && (
                            <div className="mt-1 truncate text-[10px] text-emerald-300" data-testid={`project-${p.slug}-badge-name`}>
                              ✓ {p.badge_name_de}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={p.badge_color || "#7c4dff"}
                            onChange={(e) => updateProj(p.id, "badge_color", e.target.value)}
                            data-testid={`project-${p.slug}-badge-color`}
                            className="h-9 w-12 cursor-pointer rounded border border-white/10 bg-[#10141b]"
                          />
                          <Input
                            value={p.badge_color || "#7c4dff"}
                            onChange={(e) => updateProj(p.id, "badge_color", e.target.value)}
                            className="bg-[#10141b] border-white/10 text-zinc-200 font-mono text-xs"
                            maxLength={9}
                          />
                        </div>
                        <div className="flex items-center justify-center">
                          {p.badge_code ? (
                            <div className="relative" title="Vorschau">
                              <div
                                aria-hidden
                                className="absolute inset-0 -m-1 rounded-full blur-md"
                                style={{ background: `radial-gradient(circle, ${p.badge_color || "#7c4dff"}80 0%, ${p.badge_color || "#7c4dff"}00 70%)` }}
                              />
                              <img
                                src={`https://images.habbo.com/c_images/album1584/${p.badge_code}.gif`}
                                alt={p.badge_code}
                                className="hb-pixel-img relative h-10 w-10 rounded-full"
                                style={{ boxShadow: `0 0 10px ${p.badge_color || "#7c4dff"}` }}
                              />
                            </div>
                          ) : (
                            <span className="text-[10px] text-zinc-600">(keine Vorschau)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button onClick={() => onUpdateProject(p)} className="btn-primary rounded-lg px-3 py-1.5 text-xs">
                        <Save className="mr-1 h-4 w-4" /> Projekt speichern
                      </Button>
                    </div>

                    {/* Mitglieder */}
                    <div className="mt-6">
                      <div className="text-sm font-semibold text-white">Mitglieder ({(p.members || []).length})</div>

                      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_140px_auto]">
                        <Input
                          value={newMember[p.id]?.habbo_name || ""}
                          onChange={(e) => setNewMember((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), habbo_name: e.target.value } }))}
                          placeholder="Habbo-Name"
                          className="bg-[#10141b] border-white/10 text-zinc-200"
                        />
                        <Input
                          value={newMember[p.id]?.role || ""}
                          onChange={(e) => setNewMember((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), role: e.target.value } }))}
                          placeholder="Rolle (z.B. Jury, Coordinator)"
                          className="bg-[#10141b] border-white/10 text-zinc-200"
                        />
                        <select
                          value={newMember[p.id]?.hotel || "de"}
                          onChange={(e) => setNewMember((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), hotel: e.target.value } }))}
                          className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1.5 text-sm text-zinc-200"
                          title="Habbo Hotel"
                        >
                          {hotels.map((h) => (
                            <option key={h.id} value={h.id}>{h.flag} {h.code}</option>
                          ))}
                        </select>
                        <Button onClick={() => onAddMember(p)} className="btn-primary rounded-lg px-3 py-1.5 text-xs">
                          <UserCheck className="mr-1 h-4 w-4" /> Hinzufügen
                        </Button>
                      </div>

                      <div className="mt-4 space-y-1">
                        {(p.members || []).map((m) => {
                          const hotelId = m.hotel || "de";
                          const hotelInfo = hotels.find((h) => h.id === hotelId);
                          const avatarUrl = `https://www.${hotelInfo?.domain || "habbo.de"}/habbo-imaging/avatarimage?user=${encodeURIComponent(m.habbo_name)}&direction=2&head_direction=3&size=s&action=std&gesture=sml`;
                          return (
                          <div key={m.id} className="rounded-md border border-white/5 bg-[#10141b] p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <img
                                  src={avatarUrl}
                                  alt={m.habbo_name}
                                  className="hb-pixel-img h-10"
                                  loading="lazy"
                                />
                                <div>
                                  <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                                    {m.habbo_name}
                                    {hotelInfo && hotelInfo.id !== "de" && (
                                      <span className="text-xs" title={hotelInfo.label}>{hotelInfo.flag}</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-[#9d7bff]">{m.role}</div>
                                </div>
                              </div>
                              <Button onClick={() => onRemoveMember(p, m)} className="btn-ghost rounded-lg px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40">
                                <UserX className="h-4 w-4" />
                              </Button>
                            </div>
                            {/* Background-Override pro Member */}
                            <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center">
                              <select
                                value={m.background_image_url || ""}
                                onChange={(e) => onUpdateMember(p, m, { background_image_url: e.target.value || null })}
                                data-testid={`project-${p.slug}-member-${m.id}-bg`}
                                className="flex-1 rounded-md border border-white/10 bg-[#0a0d12] px-2 py-1.5 text-xs text-zinc-200"
                              >
                                <option value="">— Kein BG-Override (nutzt User-Active-BG) —</option>
                                {backgrounds.map((b) => (
                                  <option key={b.id} value={b.image_url}>{b.name}</option>
                                ))}
                              </select>
                              {m.background_image_url && (
                                <div className="h-10 w-24 overflow-hidden rounded border border-white/10">
                                  <img src={m.background_image_url} alt="bg" className="h-full w-full object-cover" />
                                </div>
                              )}
                            </div>
                          </div>
                        );})}
                        {(p.members || []).length === 0 && (
                          <div className="py-4 text-center text-xs text-zinc-500">Noch keine Mitglieder.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
